import React from 'react';
import { isPlaceholderUrl } from '../utils/categoryImages';

function filenameFromUrl(url) {
	if (!url) return '';
	const raw = String(url);
	try {
		const u = new URL(raw, window.location.origin);
		const path = u.pathname || '';
		// Firebase Storage download URLs look like: .../o/<encodedPath>?alt=media...
		const idx = path.indexOf('/o/');
		if (idx >= 0) {
			const decoded = decodeURIComponent(path.slice(idx + 3));
			const parts = decoded.split('/').filter(Boolean);
			return parts[parts.length - 1] || decoded;
		}
		const parts = path.split('/').filter(Boolean);
		return decodeURIComponent(parts[parts.length - 1] || '');
	} catch {
		const clean = raw.split('?')[0] || raw;
		const parts = clean.split('/').filter(Boolean);
		return decodeURIComponent(parts[parts.length - 1] || '');
	}
}

/**
 * Image control that makes the built-in default image visible to the admin and
 * lets them replace it or remove it entirely.
 *
 * Shows one of four states: pending upload, custom image, built-in default, or none.
 */
export default function ImageField({
	label,
	hint,
	file,
	currentUrl,
	defaultSrc,
	useDefault,
	onFileChange,
	onRemove,
	onRestoreDefault,
	placeholderSrc = '/images/menu/placeholder.svg'
}) {
	const inputRef = React.useRef(null);
	const [filePreview, setFilePreview] = React.useState('');

	React.useEffect(() => {
		if (!file) {
			setFilePreview('');
			return undefined;
		}
		const url = URL.createObjectURL(file);
		setFilePreview(url);
		return () => URL.revokeObjectURL(url);
	}, [file]);

	// Keep the native input in sync when the parent resets the form.
	React.useEffect(() => {
		if (!file && inputRef.current) inputRef.current.value = '';
	}, [file]);

	// Same test resolveCategoryImage() uses: a stored placeholder URL is not a real image.
	const hasCustom = !!(currentUrl || '').trim() && !isPlaceholderUrl(currentUrl);
	const defaultActive = !!defaultSrc && useDefault !== false;

	// Mirrors resolveCategoryImage(): the built-in default wins until it is turned off.
	let state;
	if (file) state = 'pending';
	else if (defaultActive) state = 'default';
	else if (hasCustom) state = 'custom';
	else state = 'none';

	const previewSrc = {
		pending: filePreview,
		custom: currentUrl,
		default: defaultSrc,
		none: placeholderSrc
	}[state];

	const badge = {
		pending: { text: 'New image — not saved yet', className: 'bg-blue-500/15 text-blue-300 border-blue-400/30' },
		custom: { text: 'Custom image', className: 'bg-gold/15 text-gold border-gold/30' },
		default: { text: 'Default image (built-in)', className: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30' },
		none: { text: 'No image — placeholder shown', className: 'bg-muted-gray/15 text-muted-gray border-muted-gray/30' }
	}[state];

	const caption = {
		pending: file?.name || '',
		custom: filenameFromUrl(currentUrl),
		default: 'Comes built into the app. Replace it or remove it below.',
		none: 'Customers will see a neutral placeholder.'
	}[state];

	const canRemove = state !== 'none';
	const canRestoreDefault = !!defaultSrc && state !== 'default';

	return (
		<div className="bg-black/30 border border-gold/20 rounded-lg p-3 sm:p-4 space-y-3">
			<div>
				<p className="text-off-white font-medium text-sm">{label}</p>
				{hint ? <p className="text-xs text-muted-gray mt-0.5">{hint}</p> : null}
			</div>

			<div className="flex items-start gap-3">
				<div className="h-20 w-20 sm:h-24 sm:w-24 shrink-0 rounded-lg overflow-hidden border border-gold/20 bg-black/50">
					{previewSrc ? (
						<img
							src={previewSrc}
							alt={label}
							className="h-full w-full object-cover"
							loading="lazy"
							decoding="async"
							onError={(e) => {
								if (e.currentTarget?.dataset?.fallbackApplied) return;
								e.currentTarget.dataset.fallbackApplied = '1';
								e.currentTarget.src = placeholderSrc;
							}}
						/>
					) : null}
				</div>

				<div className="flex-1 min-w-0 space-y-2">
					<span className={`inline-block text-[11px] px-2 py-0.5 rounded-full border ${badge.className}`}>
						{badge.text}
					</span>
					{caption ? <p className="text-xs text-muted-gray break-words">{caption}</p> : null}

					<div className="flex flex-wrap gap-2 pt-1">
						<button
							type="button"
							onClick={() => inputRef.current?.click()}
							className="px-3 py-1.5 bg-gold/20 text-gold border border-gold/30 rounded hover:bg-gold/30 text-xs"
						>
							<i className="fas fa-upload mr-1"></i>
							{state === 'none' ? 'Upload image' : 'Replace image'}
						</button>

						{canRemove ? (
							<button
								type="button"
								onClick={onRemove}
								className="px-3 py-1.5 bg-red-500/15 text-red-400 border border-red-500/30 rounded hover:bg-red-500/25 text-xs"
							>
								<i className="fas fa-times mr-1"></i>
								{state === 'pending' ? 'Cancel' : 'Remove'}
							</button>
						) : null}

						{canRestoreDefault ? (
							<button
								type="button"
								onClick={onRestoreDefault}
								className="px-3 py-1.5 bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 rounded hover:bg-emerald-500/25 text-xs"
							>
								<i className="fas fa-rotate-left mr-1"></i>
								Use default image
							</button>
						) : null}
					</div>
				</div>
			</div>

			<input
				ref={inputRef}
				type="file"
				accept="image/*"
				className="hidden"
				onChange={(e) => onFileChange(e.target.files?.[0] || null)}
			/>
		</div>
	);
}

export { filenameFromUrl };
